import capitalize from 'lodash/capitalize';
import React, { use, useCallback, useMemo, useState } from 'react';

import { RecentlyVisitedClubInfo } from 'src/components/common/recently-visited-club-info';
import ClubSearch from 'src/components/layout/redesign/global-header-desktop/club-search';
import HeaderUnderlay from 'src/components/layout/redesign/global-header-desktop/header-underlay';
import HeaderDesktop from 'src/components/layout/redesign/header-desktop';
import HeaderDesktopMenu from 'src/components/layout/redesign/header-desktop/header-menu';
import PromoRibbon from 'src/components/layout/redesign/header-shared/promo-ribbon';
import RecentlyViewedClubRibbon from 'src/components/layout/redesign/header-shared/recently-viewed-club-ribbon';
import type { GlobalHeaderContent } from 'src/content/cms-provider/global-cms-provider';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import { useClientSnapshot } from 'src/custom-hooks/use-client-snapshot';
import useWindowSize from 'src/custom-hooks/use-window-size';
import { BuildType } from 'src/enums/build/build-type';
import { RedesignButtonStyle } from 'src/enums/button-style';
import { CLUBS_LINK } from 'src/enums/clubs';
import { amplitudeButtonTrackEvent } from 'src/helpers/amplitude-event-track';
import { getLocalStorageItem } from 'src/helpers/local-storage-helper';
import { RecentlyViewedClubStorageKey } from 'src/helpers/recently-viewed-club-storage-keys';
import { calculateDesktopGridWidth } from 'src/styling/constants';

const getShouldShowClubRibbon = (headerContent: GlobalHeaderContent) => {
  if (!headerContent?.headerRecentlyViewedClubRibbon?.enabled) {
    return false;
  }

  return !!getLocalStorageItem(RecentlyViewedClubStorageKey.clubId);
};

const getCTAName = (buildType: BuildType, headerContent: GlobalHeaderContent) => {
  switch (buildType) {
    case BuildType.HARBOUR:
    case BuildType.MERIDIAN:
      return 'Membership in Navigation';
    case BuildType.TOGETHER_FOUNDATION:
      return headerContent?.headerButton?.buttonText ?? 'Header Primary Button';
    default:
      return 'Join Club in Navigation';
  }
};

type GlobalHeaderDesktopProps = {
  forceDesktopHeader?: boolean;
  forcePromoRibbonVisible?: boolean;
};

const GlobalHeaderDesktop = ({
  forceDesktopHeader = false,
  forcePromoRibbonVisible = false,
}: GlobalHeaderDesktopProps) => {
  const [isClubSearchOpen, setIsClubSearchOpen] = useState(false);

  const { headerContent } = use(GlobalCmsContent);
  const { pageLevel, isDL, buildType, language } = use(SiteContext);
  const { isMobileHeader } = useWindowSize();

  const getShouldShowClubRibbonFromStorage = useCallback(
    () => getShouldShowClubRibbon(headerContent),
    [headerContent]
  );
  const shouldShowClubRibbonFromStorage = useClientSnapshot(
    getShouldShowClubRibbonFromStorage,
    false
  );
  const [hasDismissedClubRibbon, setHasDismissedClubRibbon] = useState(false);
  const shouldShowClubRibbon = shouldShowClubRibbonFromStorage && !hasDismissedClubRibbon;

  const [isRecentRibbonVisible, setIsRecentRibbonVisible] = useState<boolean>(true);

  const searchButton = useMemo(() => {
    return headerContent?.searchSettings && isDL
      ? ({
          type: 'button',
          buttonStyle: RedesignButtonStyle.WHITE_WITH_GREEN_FONT,
          handleClick: () => {
            void amplitudeButtonTrackEvent({
              buildType,
              language,
              button: null,
              pageLevel,
              ctaName: ' "Find a club" - Navigation',
              componentName: 'Global Desktop Header',
            });
            setIsClubSearchOpen(true);
            setIsRecentRibbonVisible(false);
          },
          text: headerContent.searchSettings.openSearchButtonName,
        } as const)
      : undefined;
  }, [buildType, headerContent.searchSettings, language, pageLevel, isDL]);

  const secondaryButton = useMemo(() => {
    return headerContent?.headerButtonSecondary?.showButton
      ? ({
          type: 'link',
          buttonProps: headerContent.headerButtonSecondary,
          handleClick: () => {
            void amplitudeButtonTrackEvent({
              buildType,
              language,
              button: headerContent.headerButtonSecondary,
              pageLevel,
              ctaName: capitalize(
                headerContent.headerButtonSecondary?.buttonText ?? 'header secondary button'
              ),
              ctaEventName: 'Header Secondary Button in Navigation',
              componentName: 'Global Desktop Header',
            });
          },
        } as const)
      : undefined;
  }, [buildType, headerContent.headerButtonSecondary, language, pageLevel]);

  const isTogetherFoundation = buildType === BuildType.TOGETHER_FOUNDATION;

  return (
    <HeaderDesktop
      headerContent={headerContent}
      forceDesktopHeader={forceDesktopHeader}
      primaryButton={
        headerContent?.headerButton
          ? {
              type: 'link',
              buttonProps: headerContent.headerButton,
              handleClick: () => {
                void amplitudeButtonTrackEvent({
                  buildType,
                  language,
                  button: headerContent.headerButton,
                  pageLevel,
                  ctaName: getCTAName(buildType, headerContent),
                  componentName: 'Global Desktop Header',
                  ...(isTogetherFoundation ? { ctaEventName: 'Primary Donate Button - Nav' } : {}),
                });
              },
            }
          : undefined
      }
      searchButton={searchButton}
      secondaryButton={secondaryButton}
      onMenuChanged={() => {
        setIsRecentRibbonVisible(true);
        setIsClubSearchOpen(false);
      }}
      additionalSubMenus={
        headerContent?.searchSettings && (
          <>
            <HeaderDesktopMenu
              description={headerContent.searchSettings.description}
              name={headerContent.searchSettings.title}
              visible={isClubSearchOpen}
              levelTwoMenuItems={[]}
              link={CLUBS_LINK}
              overviewText={headerContent.searchSettings.linkToMapText}
              enableOverflow
              onFinish={() => {
                setIsRecentRibbonVisible(true);
                setIsClubSearchOpen(false);
              }}
            >
              <div style={{ width: calculateDesktopGridWidth(10, 6) }}>
                <ClubSearch
                  onFinish={() => {
                    setIsClubSearchOpen(false);
                    setIsRecentRibbonVisible(true);
                  }}
                />
                <RecentlyVisitedClubInfo />
              </div>
            </HeaderDesktopMenu>
            {!isMobileHeader && isClubSearchOpen && (
              <HeaderUnderlay
                tabIndex={0}
                onClick={() => setIsClubSearchOpen(false)}
                onFocus={() => setIsClubSearchOpen(false)}
              />
            )}
          </>
        )
      }
      ribbonChildren={
        <>
          {headerContent?.headerPromoRibbon && (
            <PromoRibbon
              isCollapsed={shouldShowClubRibbon}
              promoRibbon={headerContent.headerPromoRibbon}
              forceVisible={forcePromoRibbonVisible}
            />
          )}
          <RecentlyViewedClubRibbon
            enabled={shouldShowClubRibbon && isRecentRibbonVisible}
            setShouldShowClubRibbon={(value) => {
              if (!value) setHasDismissedClubRibbon(true);
            }}
          />
        </>
      }
    />
  );
};

export default GlobalHeaderDesktop;
